import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AspectRatio,
  Box,
  Button,
  Container,
  Flex,
  Link,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  FaBookOpen,
  FaChalkboardTeacher,
  FaCompass,
  FaEnvelope,
  FaHeart,
  FaPhone,
} from 'react-icons/fa';
import {
  buildProposalUiModel,
  categoriaLabels,
} from '../components/proposals-school/proposalsSchoolUi';
import {
  getThemeFontImport,
  resolveProposalTheme,
} from '../components/proposals-standalone/proposalThemes';
import {
  StandaloneFinalCta,
  StandaloneHero,
} from '../components/proposals-standalone/StandaloneBlocks';
import landingPagesConfig from '../config/landingPagesConfig';

const targetLabels = {
  infanzia: 'Infanzia',
  primaria: 'Primaria',
  secondaria_1: 'Secondaria I',
  secondaria_2: 'Secondaria II',
};

const durationFilterOptions = [
  { value: '', label: 'Tutte' },
  { value: 'multiple', label: 'Più incontri' },
  { value: 'single', label: 'Un incontro' },
];

const keyPoints = [
  {
    icon: FaHeart,
    title: 'Accogliente per tutti',
    description:
      'Proposte pensate per classi, docenti e referenti scolastici in un linguaggio chiaro.',
  },
  {
    icon: FaChalkboardTeacher,
    title: 'Supporto didattico',
    description:
      'Ogni attività nasce per aiutare la scuola con obiettivi educativi concreti.',
  },
  {
    icon: FaCompass,
    title: 'Facile da organizzare',
    description:
      'Informazioni complete su durata, logistica e costi già in fase di scelta.',
  },
  {
    icon: FaBookOpen,
    title: 'Metodo CIL',
    description:
      'Creatività e partecipazione attiva per esperienze memorabili e formative.',
  },
];

/** Hero: file in `public/assets` (copiato da asset locale / CMS). */
const SCHOOL_LANDING_IMAGE_PLACEHOLDERS = {
  heroSrc: `${process.env.PUBLIC_URL || ''}/assets/landing-school-hero.jpg`,
  heroAlt: 'Attività e percorsi CIL con le scuole',
  introSrc: `${process.env.PUBLIC_URL || ''}/assets/landing-school-intro.jpeg`,
  introAlt: 'Percorso e contesto in aula con le classi',
  stripSrc: `${process.env.PUBLIC_URL || ''}/assets/landing-school-ambientazione.jpg`,
  stripAlt: 'Ambientazione in aula e attività con le classi',
};

const getDurationType = (durationValue) => {
  const list = Array.isArray(durationValue)
    ? durationValue
    : durationValue
      ? [durationValue]
      : [];
  if (!list.length) return null;

  const hasMultiple = list.some((entry) => {
    const normalized = String(entry).toLowerCase();
    return (
      normalized.includes('incontri') ||
      normalized.includes('ciclo') ||
      normalized.includes('percorso') ||
      normalized.includes('settiman') ||
      normalized.includes('annuale')
    );
  });
  return hasMultiple ? 'multiple' : 'single';
};

export default function SchoolProposalsLanding() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [proposals, setProposals] = useState([]);

  useEffect(() => {
    const loadProposals = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          '/api/proposte-pubbliche?landing=proposte-scuole&propostaAttiva=true'
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        setProposals(Array.isArray(payload?.data) ? payload.data : []);
      } catch (err) {
        setError('Non siamo riusciti a caricare le proposte scuole.');
      } finally {
        setLoading(false);
      }
    };

    loadProposals();
  }, []);

  const filters = useMemo(
    () => ({
      target: searchParams.get('target') || '',
      category: searchParams.get('category') || '',
      duration: searchParams.get('duration') || '',
      search: searchParams.get('search') || '',
    }),
    [searchParams]
  );

  const hasData = useMemo(() => proposals.length > 0, [proposals]);
  const theme = resolveProposalTheme('school');
  const schoolLanding = landingPagesConfig['proposte-scuole'];
  const schoolIdentitySection = schoolLanding?.identitySection;
  const schoolContacts = schoolLanding?.contacts;

  const filterOptions = useMemo(() => {
    const targets = new Map();
    const categories = new Map();

    proposals.forEach((proposal) => {
      (proposal?.activity?.target || []).forEach((target) => {
        if (target === 'famiglie') return;
        if (targetLabels[target]) {
          targets.set(target, targetLabels[target]);
        }
      });

      if (proposal?.activity?.category) {
        categories.set(
          proposal.activity.category,
          categoriaLabels[proposal.activity.category] ||
            proposal.activity.category
        );
      }
    });

    return {
      targets: Array.from(targets.entries()).map(([value, label]) => ({
        value,
        label,
      })),
      categories: Array.from(categories.entries()).map(([value, label]) => ({
        value,
        label,
      })),
    };
  }, [proposals]);

  const filteredProposals = useMemo(
    () =>
      proposals.filter((proposal) => {
        const resolved = buildProposalUiModel(proposal, { audience: 'school' });
        const targetList = Array.isArray(proposal?.activity?.target)
          ? proposal.activity.target
          : [];
        const byTarget =
          !filters.target ||
          targetList.some(
            (value) =>
              String(value).toLowerCase() === filters.target.toLowerCase()
          );
        const byCategory =
          !filters.category ||
          proposal?.activity?.category === filters.category;
        const byDuration =
          !filters.duration ||
          getDurationType(resolved.durationList) === filters.duration;
        const searchableText =
          `${resolved.title || ''} ${resolved.shortDescription || ''}`
            .toLowerCase()
            .trim();
        const bySearch =
          !filters.search ||
          searchableText.includes(filters.search.toLowerCase().trim());
        return byTarget && byCategory && byDuration && bySearch;
      }),
    [
      filters.duration,
      filters.category,
      filters.target,
      filters.search,
      proposals,
    ]
  );

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <Box bg={theme.colors.bg} minH="100vh">
      <Helmet>
        <title>Proposte per le scuole - CIL</title>
        <meta
          name="description"
          content="Scopri le proposte educative CIL per scuole, insegnanti e dirigenti."
        />
        <link rel="stylesheet" href={getThemeFontImport(theme.key)} />
      </Helmet>
      <StandaloneHero
        theme={theme}
        title="Proposte per le scuole CIL"
        subtitle="Percorsi educativi coinvolgenti, progettati per docenti e dirigenti che cercano impatto didattico e lettura immediata."
        primaryCta={{ label: 'Esplora le proposte', href: '#proposte-scuole' }}
        secondaryCta={{
          label: 'Invia richiesta dedicata',
          href: '/richiesta-scuole',
        }}
        heroImage={{
          src: SCHOOL_LANDING_IMAGE_PLACEHOLDERS.heroSrc,
          alt: SCHOOL_LANDING_IMAGE_PLACEHOLDERS.heroAlt,
        }}
      />

      {schoolIdentitySection ? (
        <Box bg="#f2f9f3" px={4} py={{ base: 10, md: 14 }}>
          <Container maxW="container.xl">
            <Stack spacing={5}>
              <Text
                textTransform="uppercase"
                letterSpacing="0.08em"
                fontWeight="800"
                fontSize="sm"
                color={theme.colors.muted}
              >
                {schoolIdentitySection.eyebrow}
              </Text>
              <Heading
                as="h2"
                color={theme.colors.text}
                fontFamily={theme.fontHeading}
                maxW="5xl"
              >
                {schoolIdentitySection.title}
              </Heading>
              <Text color={theme.colors.muted} maxW="5xl">
                {schoolIdentitySection.description}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {(schoolIdentitySection.pillars || []).map((pillar) => (
                  <Box
                    key={pillar}
                    bg="white"
                    border="1px solid"
                    borderColor={theme.colors.border}
                    borderRadius="xl"
                    p={4}
                    boxShadow="0 8px 20px rgba(36,18,58,0.08)"
                  >
                    <Text
                      color={theme.colors.text}
                      fontWeight="700"
                      fontFamily={theme.fontBody}
                      fontSize="sm"
                    >
                      {pillar}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
              <Text color={theme.colors.text} fontSize="sm" fontWeight="600">
                {schoolIdentitySection.trustNote}
              </Text>
            </Stack>
          </Container>
        </Box>
      ) : null}

      <Box px={4} pb={{ base: 8, md: 10 }} mt={{ base: 4, md: 6 }}>
        <Container maxW="container.xl">
          <Box
            borderRadius="3xl"
            p={{ base: 5, md: 8 }}
            bg={theme.colors.surface}
            border="1px solid"
            borderColor={theme.colors.border}
            boxShadow="0 20px 45px rgba(36,18,58,0.18)"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              right="-30px"
              top="-30px"
              w="140px"
              h="140px"
              borderRadius="full"
              bg={theme.colors.accentAlt}
              opacity={0.18}
            />
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              spacing={{ base: 8, lg: 10 }}
              alignItems="center"
            >
              <Stack spacing={3}>
                <Heading
                  as="h2"
                  fontFamily={theme.fontHeading}
                  color={theme.colors.text}
                  fontSize={{ base: '2xl', md: '4xl' }}
                >
                  Orientamento chiaro per docenti e coordinatori
                </Heading>
                <Text
                  color={theme.colors.muted}
                  fontSize={{ base: 'md', md: 'lg' }}
                >
                  Ogni proposta è presentata con informazioni essenziali sul
                  percorso, il contesto target e gli aspetti logistici. Usa i
                  filtri per restringere la scelta oppure vai direttamente alla
                  richiesta dedicata quando serve un progetto su misura.
                </Text>
              </Stack>
              <Box w="100%">
                <AspectRatio ratio={4 / 3}>
                  <Image
                    src={SCHOOL_LANDING_IMAGE_PLACEHOLDERS.introSrc}
                    alt={SCHOOL_LANDING_IMAGE_PLACEHOLDERS.introAlt}
                    borderRadius="2xl"
                    objectFit="cover"
                    boxShadow="0 12px 32px rgba(36,18,58,0.15)"
                    border="1px solid"
                    borderColor={theme.colors.border}
                  />
                </AspectRatio>
              </Box>
            </SimpleGrid>
          </Box>
        </Container>
      </Box>

      <Container maxW="container.xl" py={{ base: 10, md: 14 }}>
        <Stack spacing={8}>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
            {keyPoints.map((point) => (
              <Box
                key={point.title}
                bg={theme.colors.surface}
                borderWidth="1px"
                borderColor={theme.colors.border}
                borderRadius="2xl"
                p={5}
                boxShadow="0 8px 20px rgba(36, 18, 58, 0.12)"
                transition="transform 160ms ease"
                _hover={{ transform: 'translateY(-3px)' }}
              >
                <Stack spacing={2}>
                  <Icon
                    as={point.icon}
                    boxSize={5}
                    color={theme.colors.accentAlt}
                  />
                  <Heading
                    as="h3"
                    size="sm"
                    color={theme.colors.text}
                    fontFamily={theme.fontHeading}
                  >
                    {point.title}
                  </Heading>
                  <Text
                    fontSize="sm"
                    color={theme.colors.muted}
                    fontFamily={theme.fontBody}
                  >
                    {point.description}
                  </Text>
                </Stack>
              </Box>
            ))}
          </SimpleGrid>

          <Box w="100%">
            <AspectRatio
              ratio={{ base: 16 / 10, md: 21 / 9 }}
              maxH={{ md: '320px' }}
            >
              <Image
                src={SCHOOL_LANDING_IMAGE_PLACEHOLDERS.stripSrc}
                alt={SCHOOL_LANDING_IMAGE_PLACEHOLDERS.stripAlt}
                borderRadius="2xl"
                objectFit="cover"
                w="100%"
                h="100%"
                border="1px solid"
                borderColor={theme.colors.border}
                boxShadow="0 14px 36px rgba(36,18,58,0.12)"
              />
            </AspectRatio>
          </Box>

          <Heading
            as="h2"
            fontSize={{ base: 'xl', md: '2xl' }}
            fontFamily={theme.fontHeading}
            color={theme.colors.text}
            textAlign={{ base: 'left', md: 'center' }}
            mb={{ base: 4, md: 5 }}
          >
            Ecco le nostre proposte
          </Heading>

          <Box
            bg={theme.colors.surface}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={theme.colors.border}
            overflow="hidden"
            boxShadow="0 10px 24px rgba(36, 18, 58, 0.12)"
          >
            <Box display={{ base: 'block', md: 'none' }}>
              <Accordion allowToggle defaultIndex={[]} reduceMotion>
                <AccordionItem border="none">
                  <Box as="h2" m={0}>
                    <AccordionButton
                      py={5}
                      px={6}
                      bg={theme.colors.surface}
                      _hover={{ bg: 'gray.50' }}
                      _expanded={{ bg: 'gray.50' }}
                    >
                      <Box
                        flex="1"
                        textAlign="left"
                        color={theme.colors.text}
                        fontFamily={theme.fontHeading}
                        fontWeight="700"
                        fontSize="md"
                      >
                        Filtra le proposte
                      </Box>
                      <AccordionIcon color={theme.colors.text} />
                    </AccordionButton>
                  </Box>
                  <AccordionPanel
                    px={6}
                    pb={6}
                    pt={0}
                    bg={theme.colors.surface}
                  >
                    <SimpleGrid columns={1} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Target</FormLabel>
                        <Select
                          bg="white"
                          value={filters.target}
                          onChange={(e) =>
                            updateFilter('target', e.target.value)
                          }
                        >
                          <option value="">Tutti</option>
                          {(filterOptions.targets || []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Categoria</FormLabel>
                        <Select
                          bg="white"
                          value={filters.category}
                          onChange={(e) =>
                            updateFilter('category', e.target.value)
                          }
                        >
                          <option value="">Tutte</option>
                          {(filterOptions.categories || []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Durata</FormLabel>
                        <Select
                          bg="white"
                          value={filters.duration}
                          onChange={(e) =>
                            updateFilter('duration', e.target.value)
                          }
                        >
                          {durationFilterOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Search</FormLabel>
                        <Input
                          bg="white"
                          value={filters.search}
                          onChange={(e) =>
                            updateFilter('search', e.target.value)
                          }
                          placeholder="Cerca per titolo o descrizione"
                        />
                      </FormControl>
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>

            <Box
              display={{ base: 'none', md: 'block' }}
              px={6}
              py={5}
              bg={theme.colors.surface}
            >
              <SimpleGrid columns={{ md: 3 }} spacing={4}>
                <FormControl>
                  <Flex align="center" gap={3}>
                    <FormLabel
                      fontSize="sm"
                      minW="72px"
                      mb={0}
                      color={theme.colors.text}
                    >
                      Target
                    </FormLabel>
                    <Select
                      bg="white"
                      value={filters.target}
                      onChange={(e) => updateFilter('target', e.target.value)}
                    >
                      <option value="">Tutti</option>
                      {(filterOptions.targets || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                </FormControl>
                <FormControl>
                  <Flex align="center" gap={3}>
                    <FormLabel
                      fontSize="sm"
                      minW="72px"
                      mb={0}
                      color={theme.colors.text}
                    >
                      Categoria
                    </FormLabel>
                    <Select
                      bg="white"
                      value={filters.category}
                      onChange={(e) => updateFilter('category', e.target.value)}
                    >
                      <option value="">Tutte</option>
                      {(filterOptions.categories || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                </FormControl>
                <FormControl>
                  <Flex align="center" gap={3}>
                    <FormLabel
                      fontSize="sm"
                      minW="72px"
                      mb={0}
                      color={theme.colors.text}
                    >
                      Durata
                    </FormLabel>
                    <Select
                      bg="white"
                      value={filters.duration}
                      onChange={(e) => updateFilter('duration', e.target.value)}
                    >
                      {durationFilterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Flex>
                </FormControl>
              </SimpleGrid>
              <SimpleGrid columns={{ md: 2 }} spacing={4} mt={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Search</FormLabel>
                  <Input
                    bg="white"
                    value={filters.search}
                    onChange={(e) => updateFilter('search', e.target.value)}
                    placeholder="Cerca per titolo o descrizione"
                  />
                </FormControl>
              </SimpleGrid>
            </Box>
          </Box>

          {loading ? (
            <Stack direction="row" align="center">
              <Spinner color="#36a9e1" />
              <Text>Caricamento proposte...</Text>
            </Stack>
          ) : null}

          {!loading && error ? (
            <Box
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.200"
              p={5}
              borderRadius="lg"
            >
              <Text mb={3}>{error}</Text>
              <Button as={RouterLink} to="/contattaci" size="sm">
                Vai ai contatti
              </Button>
            </Box>
          ) : null}

          {!loading && !error && !hasData ? (
            <Box
              bg="white"
              borderRadius="lg"
              p={5}
              borderWidth="1px"
              borderColor="gray.200"
            >
              <Text mb={3}>Nessuna proposta scuole attiva al momento.</Text>
              <Button
                as={RouterLink}
                to="/richiesta-scuole"
                size="sm"
                bg="#ffcc00"
                color="black"
              >
                Invia richiesta dedicata
              </Button>
            </Box>
          ) : null}

          {!loading && !error && hasData ? (
            <Box id="proposte-scuole" scrollMarginTop="200px">
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
                {filteredProposals.map((proposal) => {
                  const model = buildProposalUiModel(proposal, {
                    audience: 'school',
                  });
                  const detailPath = `/proposte-scuole/${proposal.slug}`;
                  return (
                    <Stack
                      key={proposal.slug || proposal.documentId}
                      spacing={3}
                    >
                      <Box
                        as={RouterLink}
                        to={detailPath}
                        role="group"
                        bg={theme.colors.surface}
                        borderRadius="24px"
                        borderWidth="2px"
                        borderColor={theme.colors.border}
                        boxShadow="0 18px 40px rgba(36,18,58,0.22)"
                        overflow="hidden"
                        textDecoration="none"
                        transition="transform 180ms ease, box-shadow 180ms ease"
                        _hover={{
                          transform: 'translateY(-8px)',
                          boxShadow: '0 26px 56px rgba(36,18,58,0.32)',
                        }}
                      >
                        {model.heroImageUrl ? (
                          <Box
                            bg="blackAlpha.50"
                            borderBottom="12px solid"
                            borderColor={theme.colors.surface}
                          >
                            <Image
                              src={model.heroImageUrl}
                              alt={model.title}
                              w="100%"
                              h="220px"
                              objectFit="cover"
                              transform="scale(1.02)"
                              transition="transform 220ms ease"
                              _groupHover={{ transform: 'scale(1.06)' }}
                            />
                          </Box>
                        ) : null}
                        <Box
                          bg="white"
                          borderTopRadius={model.heroImageUrl ? 0 : '24px'}
                          p={5}
                          pt={model.heroImageUrl ? 4 : 6}
                        >
                          <Heading
                            as="h2"
                            fontSize="lg"
                            textAlign="center"
                            fontFamily={theme.fontHeading}
                            color={theme.colors.text}
                            mb={2}
                            noOfLines={3}
                          >
                            {model.title}
                          </Heading>
                          {model.shortDescription ? (
                            <Text
                              fontSize="sm"
                              textAlign="center"
                              color={theme.colors.muted}
                              noOfLines={3}
                            >
                              {model.shortDescription}
                            </Text>
                          ) : null}
                        </Box>
                      </Box>
                    </Stack>
                  );
                })}
              </SimpleGrid>
            </Box>
          ) : null}

          {!loading && !error && hasData && filteredProposals.length === 0 ? (
            <Box
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.200"
              p={5}
              borderRadius="lg"
            >
              <Text mb={2}>
                Nessuna proposta corrisponde ai filtri selezionati.
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSearchParams(new URLSearchParams())}
              >
                Azzera filtri
              </Button>
            </Box>
          ) : null}

          <StandaloneFinalCta
            theme={theme}
            title="Vuoi una proposta personalizzata per la tua scuola?"
            text="Raccontaci il contesto della classe e costruiamo insieme il percorso più adatto."
            primaryCta={{
              label: 'Richiedi una proposta per la tua scuola',
              href: '/richiesta-scuole',
            }}
          />

          {schoolContacts?.email || schoolContacts?.phone ? (
            <Box
              bg={theme.colors.surface}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={theme.colors.border}
              p={{ base: 5, md: 6 }}
              boxShadow="0 10px 24px rgba(36, 18, 58, 0.1)"
            >
              <Stack spacing={4} align="center" textAlign="center">
                <Heading
                  as="h2"
                  size="md"
                  color={theme.colors.text}
                  fontFamily={theme.fontHeading}
                >
                  Contatti
                </Heading>
                <Text
                  color={theme.colors.muted}
                  fontSize="sm"
                  fontFamily={theme.fontBody}
                  maxW="lg"
                >
                  Per informazioni o per parlare con il team educativo:
                </Text>
                <Flex
                  direction={{ base: 'column', sm: 'row' }}
                  gap={{ base: 3, sm: 8 }}
                  justify="center"
                  align="center"
                  flexWrap="wrap"
                >
                  {schoolContacts.email ? (
                    <Link
                      href={`mailto:${schoolContacts.email}`}
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      color={theme.colors.text}
                      fontWeight="700"
                      fontFamily={theme.fontBody}
                      fontSize="md"
                      _hover={{ color: theme.colors.accentAlt }}
                    >
                      <Icon as={FaEnvelope} color={theme.colors.accentAlt} />
                      {schoolContacts.email}
                    </Link>
                  ) : null}
                  {schoolContacts.phone ? (
                    <Link
                      href={`tel:${schoolContacts.phoneTel || schoolContacts.phone}`}
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      color={theme.colors.text}
                      fontWeight="700"
                      fontFamily={theme.fontBody}
                      fontSize="md"
                      _hover={{ color: theme.colors.accentAlt }}
                    >
                      <Icon as={FaPhone} color={theme.colors.accentAlt} />
                      {schoolContacts.phone}
                    </Link>
                  ) : null}
                </Flex>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}
